import { Camera, CameraResultType, CameraSource, Photo } from '@capacitor/camera';
import { RefObject, useState } from 'react';
import { Filesystem } from '@capacitor/filesystem';
import { isPlatform, IonIcon, IonImg, IonSpinner } from '@ionic/react';
import { _ACTIVITY_ROOT, _photoPrefix } from './constants';
import imageCompression from 'browser-image-compression';
import { imageOutline, videocam } from 'ionicons/icons';

async function base64FromPath(path: string): Promise<string> {
    const response = await fetch(path);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = reject;
      reader.onload = () => {
        if (typeof reader.result === 'string') {
          resolve(reader.result);
        } else {
          reject('method did not return a string');
        }
      };
      reader.readAsDataURL(blob);
    });
}

async function compressBase64Image(base64Image: string): Promise<string> {
  // Convert base64 string to a File object
  const response = await fetch(base64Image);
  const data = await response.blob();
  const file = new File([data], 'image.jpg', { type: data.type });

  // Compress the image
  const options = {
    maxSizeMB: 0.05,//0.035,
    maxWidthOrHeight: 140,
    useWebWorker: true
  };
  const compressedFile = await imageCompression(file, options);

  // Convert the compressed File object to a base64 string
  return new Promise(resolve => {
    const reader = new FileReader();
    reader.onload = event => resolve(event.target!.result as string);
    reader.readAsDataURL(compressedFile);
  });
}



export function usePhotoGallery() {
    const [photo, setPhoto] = useState<string>();

    const savePicture = async (photo: Photo, compress: boolean = true): Promise<string> => {
        let base64Data: string;
        // "hybrid" will detect Cordova or Capacitor;
        if (isPlatform('hybrid')) {
          const file = await Filesystem.readFile({
            path: photo.path!,
          });
          base64Data = file.data;
        } else {
          base64Data = await base64FromPath(photo.webPath!);
        }
        if (compress) {
          return compressBase64Image(base64Data);
        } else {
          return base64Data;
        }
      };
    
    
      
    const takePhoto = async (compress:boolean = true) => {
        try {
          const photo = await Camera.getPhoto({
            resultType: CameraResultType.Uri,
            source: CameraSource.Camera,
            quality: compress ? 90 : 100,
          });
          setPhoto(await savePicture(photo, compress));
        } catch (error) {
          // Handle error
          console.log("Error taking photo:", error);
        }
    };
      
  
      return {
        photo,
        takePhoto,
        setPhoto
    };
  }

  export const getYoutubeVideoID = (url:string) => {
    // For standard YouTube URLs
    if (url.includes('watch?v=')) {
      return url.split('watch?v=')[1].split('&')[0];
    }
    // For shortened YouTube URLs
    else if (url.includes('youtu.be/')) {
      return url.split('youtu.be/')[1].split('?')[0];
    }
    return null;
  };

  export const isYoutubeUrl = (url:string) => {
    const regex = /^(https?:\/\/)?(www\.youtube\.com|youtu\.?be)\/.+$/;
    return regex.test(url);
  };
  

  export function displayPic(
    photo: string | undefined | null,
    parentMode: boolean,
    spinnerIfNoImage: boolean = false,
    height?: string | undefined,
    disableClickStopPropagation?: boolean
  ) {
    const imageUrl = getImageUrl(photo);
    
  
    if (imageUrl) {
      return (
        <div  >
          {photo && isYoutubeUrl(photo) ? <IonIcon icon={videocam} /> : null}
          <img
          className='card-image'
          draggable="false"
          src={imageUrl} alt="thumbnail" 
          onClick={disableClickStopPropagation ? (e) => {
            e.preventDefault();  // Prevent default action
          e.stopPropagation(); // Stop the event from bubbling up
        } : undefined}
          />
        </div>
      );
    }
  
    if (!photo || photo === null || photo.trim().length < 20) {
      if (spinnerIfNoImage) {
        return (
          <div style={{ maxHeight: height ? height : '400px', overflow: 'hidden' }}>
            <IonSpinner />
          </div>
        );
      } else if (parentMode) {
        return (
          <div style={{ maxHeight: height ? height : '300px', overflow: 'hidden' }}>
            <IonIcon style={{ width: '100%', height: '60px' }} icon={imageOutline} />
          </div>
        );
      }
    }
  
    return null; // Return null if conditions for rendering the image or spinner are not met.
  }
  

export  function getImageUrl(photo: string | undefined | null): string | null {
    if (photo && isYoutubeUrl(photo)) {
      const videoID = getYoutubeVideoID(photo);
      return videoID ? `https://img.youtube.com/vi/${videoID}/mqdefault.jpg` : 'path_to_default_thumbnail_if_no_video_id';
    }
  
    if (!photo || photo === null || photo.trim().length < 20) {
      return null;
    }
  
    return photo; // Return the photo URL for valid, non-YouTube URLs.
  }
  
  



